import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../api/axios';
import { useAuth } from '../context/AuthContext';
import '../styles/Chat.css';

const TYPING_TIMEOUT_MS = 3000;
const WS_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8080')
  .replace(/^http/, 'ws');

export default function ChatPage() {
  const { id: matchId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [conversation, setConversation] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const [showPlatinumModal, setShowPlatinumModal] = useState(false);

  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimerRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const matchRes = await API.get(`/api/matches/${matchId}`);
        if (cancelled) return;
        setOtherUser(matchRes.data.other_user);

        const convRes = await API.get(`/api/matches/${matchId}/conversation`);
        if (cancelled) return;
        setConversation(convRes.data);

        const historyRes = await API.get(
          `/api/conversations/${convRes.data.id}/messages`
        );
        if (cancelled) return;
        setMessages([...(historyRes.data.messages || [])].reverse());

        await API.put(`/api/conversations/${convRes.data.id}/read`);
      } catch (err) {
        if (!cancelled) {
          console.error('Chat init error:', err);
          navigate('/matches');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    return () => { cancelled = true; };
  }, [matchId, navigate]);

  useEffect(() => {
    if (!conversation) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const ws = new WebSocket(`${WS_BASE}/ws?token=${token}`);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        handleWsEvent(msg);
      } catch (e) {}
    };

    ws.onerror = (e) => console.warn('WS error', e);
    ws.onclose = () => {};

    return () => {
      ws.close();
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id]);

  const handleWsEvent = useCallback(
    (event) => {
      if (!conversation) return;

      switch (event.type) {
        case 'message:new': {
          const msg = event.data;
          if (msg.conversation_id !== conversation.id) return;
          setMessages((prev) => [...prev, msg]);
          API.put(`/api/conversations/${conversation.id}/read`).catch(() => {});
          break;
        }
        case 'typing:start': {
          if (event.data.conversation_id !== conversation.id) return;
          setOtherTyping(true);
          break;
        }
        case 'typing:stop': {
          if (event.data.conversation_id !== conversation.id) return;
          setOtherTyping(false);
          break;
        }
        case 'message:read': {
          if (event.data.conversation_id !== conversation.id) return;
          setMessages((prev) =>
            prev.map((m) =>
              m.sender_id === user.id && !m.read_at
                ? { ...m, read_at: new Date().toISOString() }
                : m
            )
          );
          break;
        }
        default:
          break;
      }
    },
    [conversation, user]
  );

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, otherTyping]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || sending || !conversation) return;

    setSending(true);
    setDraft('');
    sendTypingStop();

    try {
      const response = await API.post(
        `/api/conversations/${conversation.id}/messages`,
        { content: text, type: 'text' }
      );
      setMessages((prev) => [...prev, response.data]);
    } catch (err) {
      if (err.response?.data?.error === 'conversation_limit_reached') {
        setShowPlatinumModal(true);
        setDraft(text);
      } else if (err.response?.data?.error === 'premium_required') {
        if (window.confirm('Upgrade to Godly+ to send messages. Go to upgrade page?')) {
          navigate('/premium');
        }
      } else {
        setDraft(text);
      }
    } finally {
      setSending(false);
    }
  };

  const sendTypingStart = () => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !conversation) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current < 2000) return;
    lastTypingSentRef.current = now;
    ws.send(JSON.stringify({
      type: 'typing:start',
      data: { conversation_id: conversation.id },
    }));
  };

  const sendTypingStop = () => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !conversation) return;
    ws.send(JSON.stringify({
      type: 'typing:stop',
      data: { conversation_id: conversation.id },
    }));
    lastTypingSentRef.current = 0;
  };

  const handleDraftChange = (e) => {
    setDraft(e.target.value);
    sendTypingStart();
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      sendTypingStop();
    }, TYPING_TIMEOUT_MS);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (loading) {
    return (
      <div className="chat-page">
        <ChatHeader otherUser={null} onBack={() => navigate('/matches')} />
        <div className="chat-loading">Loading messages...</div>
      </div>
    );
  }

  return (
    <div className="chat-page">
      <ChatHeader otherUser={otherUser} onBack={() => navigate('/matches')} />

      <div className="chat-messages" ref={messagesContainerRef}>
        {messages.length === 0 ? (
          <div className="chat-empty">
            <p>You matched with {otherUser?.full_name}!</p>
            <p className="chat-empty-hint">Say hello and break the ice.</p>
          </div>
        ) : (
          renderMessagesWithDayBreaks(messages, user.id)
        )}

        {otherTyping && (
          <div className="typing-indicator">
            <div className="typing-bubble">
              <span></span><span></span><span></span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-bar">
        <textarea
          className="chat-input"
          placeholder="Type a message..."
          value={draft}
          onChange={handleDraftChange}
          onKeyPress={handleKeyPress}
          rows={1}
          disabled={sending}
        />
        <button
          className="chat-send-button"
          onClick={handleSend}
          disabled={sending || !draft.trim()}
        >
          ➤
        </button>
      </div>

      {showPlatinumModal && (
        <div className="modal-overlay" onClick={() => setShowPlatinumModal(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h2>Daily Limit Reached</h2>
            <p>
              Godly+ users can start 1 new conversation per day.
              Upgrade to Platinum for unlimited conversations and more.
            </p>
            <div className="platinum-modal-features">
              <p>✓ Unlimited conversations</p>
              <p>✓ Rewind swipes</p>
              <p>✓ Unlimited likes</p>
              <p>✓ Priority in discovery</p>
            </div>
            <div className="modal-actions">
              <button
                className="modal-cancel"
                onClick={() => setShowPlatinumModal(false)}
              >
                Maybe later
              </button>
              <button
                className="modal-confirm-platinum"
                onClick={() => {
                  setShowPlatinumModal(false);
                  navigate('/premium');
                }}
              >
                Upgrade to Platinum
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ChatHeader({ otherUser, onBack }) {
  const navigate = useNavigate();
  const openProfile = () => {
    if (otherUser?.user_id) {
      navigate(`/users/${otherUser.user_id}`);
    }
  };

  return (
    <header className="chat-header">
      <button className="chat-back" onClick={onBack} aria-label="Back">←</button>
      {otherUser && (
        <div className="chat-header-clickable" onClick={openProfile} role="button" tabIndex={0}>
          {otherUser.photos?.[0]?.url ? (
            <img
              src={otherUser.photos[0].url}
              alt={otherUser.full_name}
              className="chat-header-avatar"
            />
          ) : (
            <div className="chat-header-avatar-placeholder">
              {otherUser.full_name?.[0] || '?'}
            </div>
          )}
          <div className="chat-header-info">
            <h2>{otherUser.full_name}</h2>
            {otherUser.denomination && (
              <span className="chat-header-sub">{otherUser.denomination}</span>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function MessageBubble({ message, isMine }) {
  return (
    <div className={`message-row ${isMine ? 'mine' : 'theirs'}`}>
      <div className="message-bubble">
        <div className="message-content">{message.content}</div>
        <div className="message-meta">
          {formatTime(message.created_at)}
          {isMine && message.read_at && <span className="read-tick">✓✓</span>}
          {isMine && !message.read_at && <span className="sent-tick">✓</span>}
        </div>
      </div>
    </div>
  );
}

function DayBreak({ date }) {
  return (
    <div className="day-break">
      <span>{formatDayLabel(date)}</span>
    </div>
  );
}

function renderMessagesWithDayBreaks(messages, currentUserId) {
  const elements = [];
  let lastDate = null;

  messages.forEach((msg) => {
    const msgDate = new Date(msg.created_at).toDateString();
    if (msgDate !== lastDate) {
      elements.push(<DayBreak key={`day-${msg.id}`} date={msg.created_at} />);
      lastDate = msgDate;
    }
    elements.push(
      <MessageBubble
        key={msg.id}
        message={msg}
        isMine={msg.sender_id === currentUserId}
      />
    );
  });

  return elements;
}

function formatTime(iso) {
  const d = new Date(iso);
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

function formatDayLabel(iso) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';

  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}